import type { TLShape, TLHandle } from "@tldraw/tlschema";
import type { Vec } from "../geometry";
import type { PlanroomEditor } from "../editor";
import type { ResizeHandle } from "./shape-util";

// Input events and the hierarchical state machine that tools are built from.

type Modifiers = {
  shiftKey: boolean;
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
};
export type PointerTarget =
  | { target: "canvas"; shape?: undefined }
  | { target: "shape"; shape: TLShape }
  | { target: "selection"; handle?: ResizeHandle | "rotate"; shape?: undefined }
  | { target: "handle"; shape: TLShape; handle: TLHandle };
export type PointerEventInfo = Modifiers &
  PointerTarget & {
    type: "pointer";
    name: "pointer_down" | "pointer_move" | "pointer_up" | "long_press";
    point: Vec;
    pagePoint: Vec;
    button: number;
    pointerId: number;
  };
export type ClickEventInfo = Modifiers &
  PointerTarget & {
    type: "click";
    name: "double_click" | "triple_click";
    phase: "down" | "up";
    point: Vec;
    pagePoint: Vec;
    button: number;
  };
export type KeyboardEventInfo = Modifiers & {
  type: "keyboard";
  name: "key_down" | "key_up" | "key_repeat";
  key: string;
  code: string;
};
export type WheelEventInfo = Modifiers & {
  type: "wheel";
  name: "wheel";
  delta: Vec;
  point: Vec;
};
export type MiscEventInfo = {
  type: "misc";
  name: "cancel" | "complete" | "interrupt" | "tick";
};
export type EventInfo =
  | PointerEventInfo
  | ClickEventInfo
  | KeyboardEventInfo
  | WheelEventInfo
  | MiscEventInfo;

export type StateNodeConstructor = {
  new (editor: PlanroomEditor, parent?: StateNode): StateNode;
  id: string;
  initial?: string;
  children?: () => StateNodeConstructor[];
  isLockable?: boolean;
};

export class StateNode {
  static id: string;
  static initial?: string;
  static children?: () => StateNodeConstructor[];
  static isLockable = true;

  readonly id: string;
  readonly children?: Record<string, StateNode>;
  private current?: StateNode;
  private active = false;
  /** The info the state was last entered with. */
  info: unknown;

  constructor(
    public editor: PlanroomEditor,
    public parent: StateNode = undefined as unknown as StateNode,
  ) {
    const ctor = this.constructor as StateNodeConstructor;
    this.id = ctor.id;
    const kids = ctor.children?.();
    if (kids?.length) {
      this.children = {};
      for (const Kid of kids) this.children[Kid.id] = new Kid(editor, this);
    }
  }

  getCurrent() {
    return this.current;
  }
  getIsActive() {
    return this.active;
  }
  /** Dotted path from this node down to the deepest active state. */
  getPath(): string {
    return this.current ? `${this.id}.${this.current.getPath()}` : this.id;
  }

  /** Move to a child state (a dotted path descends further). */
  transition(path: string, info: unknown = {}) {
    const [id, ...rest] = path.split(".");
    const next = this.children?.[id];
    if (!next) throw new Error(`${this.id} has no state ${id}`);
    const from = this.current?.id ?? "";
    if (this.current !== next) {
      this.current?.exit(info, id);
      this.current = next;
      next.enter(info, from, rest.length === 0);
    }
    if (rest.length) next.transition(rest.join("."), info);
    return this;
  }

  enter(info: unknown, from: string, enterInitial = true) {
    this.active = true;
    this.info = info;
    this.onEnter?.(info as never, from);
    const initial = (this.constructor as StateNodeConstructor).initial;
    if (enterInitial && initial && this.children && !this.current)
      this.transition(initial, info);
  }

  exit(info: unknown, to: string) {
    this.current?.exit(info, to);
    this.current = undefined;
    this.active = false;
    this.onExit?.(info as never, to);
  }

  /** Deliver an event to the deepest active state first, then this one. */
  handleEvent(info: EventInfo) {
    const current = this.current;
    current?.handleEvent(info);
    if (!this.active) return;
    const handler = HANDLERS[info.name];
    if (handler && current === this.current) {
      const method = (this as unknown as Record<string, unknown>)[handler];
      if (typeof method === "function") method.call(this, info);
    }
  }

  onEnter?(info: any, from: string): void;
  onExit?(info: any, to: string): void;
  onPointerDown?(info: PointerEventInfo): void;
  onPointerMove?(info: PointerEventInfo): void;
  onPointerUp?(info: PointerEventInfo): void;
  onLongPress?(info: PointerEventInfo): void;
  onDoubleClick?(info: ClickEventInfo): void;
  onTripleClick?(info: ClickEventInfo): void;
  onKeyDown?(info: KeyboardEventInfo): void;
  onKeyUp?(info: KeyboardEventInfo): void;
  onKeyRepeat?(info: KeyboardEventInfo): void;
  onWheel?(info: WheelEventInfo): void;
  onCancel?(info: MiscEventInfo): void;
  onComplete?(info: MiscEventInfo): void;
  onInterrupt?(info: MiscEventInfo): void;
  onTick?(info: MiscEventInfo): void;
}

const HANDLERS: Record<string, string> = {
  pointer_down: "onPointerDown",
  pointer_move: "onPointerMove",
  pointer_up: "onPointerUp",
  long_press: "onLongPress",
  double_click: "onDoubleClick",
  triple_click: "onTripleClick",
  key_down: "onKeyDown",
  key_up: "onKeyUp",
  key_repeat: "onKeyRepeat",
  wheel: "onWheel",
  cancel: "onCancel",
  complete: "onComplete",
  interrupt: "onInterrupt",
  tick: "onTick",
};
